import { Component } from '@theme/component';
import {
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  productsMatchingTitle,
  scoreProductTitle,
} from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;
    const { searchInput } = this.refs;

    this.#bindLeadingSpaceRestriction(searchInput, signal);
    this.#stripLeadingWhitespace(searchInput);

    if (searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      searchInput.addEventListener('focus', this.#handleSearchFocus, { signal });
    }
  }

  /**
   * Blocks leading whitespace in the search field (typed spaces, paste, IME).
   * @param {HTMLInputElement} searchInput
   * @param {AbortSignal} signal
   */
  #bindLeadingSpaceRestriction(searchInput, signal) {
    if (searchInput.dataset.leadingSpaceBound === 'true') return;
    searchInput.dataset.leadingSpaceBound = 'true';

    searchInput.addEventListener(
      'keydown',
      (event) => {
        const isSpace = event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space';
        if (!isSpace) return;
        if (searchInput.selectionStart === 0) {
          event.preventDefault();
        }
      },
      { signal }
    );

    searchInput.addEventListener(
      'beforeinput',
      (event) => {
        if (searchInput.selectionStart !== 0) return;
        if (typeof event.data !== 'string' || !/^\s/.test(event.data)) return;

        if (/^\s+$/.test(event.data)) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        const stripped = event.data.replace(/^\s+/, '');
        if (!stripped) return;

        const { selectionStart, selectionEnd, value } = searchInput;
        const start = selectionStart ?? 0;
        const end = selectionEnd ?? start;
        searchInput.value = `${value.slice(0, start)}${stripped}${value.slice(end)}`;
        const caret = start + stripped.length;
        searchInput.setSelectionRange(caret, caret);
        searchInput.dispatchEvent(
          new InputEvent('input', { bubbles: true, inputType: 'insertFromPaste', data: stripped })
        );
      },
      { signal }
    );

    searchInput.addEventListener(
      'input',
      () => {
        this.#stripLeadingWhitespace(searchInput);
      },
      { signal }
    );
  }

  /**
   * Removes leading whitespace while preserving the caret when possible.
   * @param {HTMLInputElement} searchInput
   */
  #stripLeadingWhitespace(searchInput) {
    const stripped = searchInput.value.replace(/^\s+/, '');
    if (stripped === searchInput.value) return;

    const offset = searchInput.value.length - stripped.length;
    const start = Math.max(0, (searchInput.selectionStart ?? 0) - offset);
    const end = Math.max(0, (searchInput.selectionEnd ?? 0) - offset);
    searchInput.value = stripped;
    searchInput.setSelectionRange(start, end);
  }

  /**
   * Loads the empty state when an inline search field receives focus.
   */
  #handleSearchFocus = () => {
    const searchTerm = this.refs.searchInput.value.trim();

    if (searchTerm.length > 0) {
      this.#showResetButton();
      this.#getSearchResults(searchTerm);
      return;
    }

    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    if (this.refs.searchInput.value.length > 0) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__queries-list, ' +
          '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else if (this.refs.form) {
          event.preventDefault();
          this.refs.form.requestSubmit();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    this.#stripLeadingWhitespace(this.refs.searchInput);
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      const content = this.querySelector('.predictive-search-form__content');

      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
        resultsInner.scrollLeft = 0;
      }

      if (content instanceof HTMLElement) {
        content.scrollTop = 0;
        content.scrollLeft = 0;
      }
    });
  }

  /**
   * @param {string} searchTerm
   * @returns {URL}
   */
  #predictiveSearchUrl(searchTerm) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('options[prefix]', 'last');
    if (Theme.settings?.hideSoldOutProducts) {
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
    }
    return url;
  }

  /**
   * @param {Document} doc
   * @returns {HTMLElement | null}
   */
  #ensurePredictiveProductList(doc) {
    const existing = doc.querySelector(
      '.predictive-search-results__section--products .predictive-search-results__wrapper-products'
    );
    if (existing instanceof HTMLElement) return existing;

    const section = doc.createElement('div');
    section.className = 'predictive-search-results__section predictive-search-results__section--products';

    const heading = doc.createElement('h4');
    heading.className = 'predictive-search-results__title';
    heading.textContent = Theme.translations?.products || 'Products';

    const list = doc.createElement('ul');
    list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';
    section.append(heading, list);

    const noResults = doc.querySelector('.predictive-search-results__section--no-results');
    if (noResults) {
      noResults.replaceWith(section);
      return list;
    }

    const inner = doc.querySelector('[data-search-results]') || doc.body;
    inner.append(section);
    return list;
  }

  /**
   * @param {Document} doc
   * @param {string} title
   * @param {string} handle
   * @param {number} id
   * @param {string} image
   * @param {boolean} available
   * @returns {HTMLLIElement}
   */
  #createPredictiveProductRow(doc, title, handle, id, image, available) {
    const item = doc.createElement('li');
    item.className =
      'predictive-search-results__card predictive-search-results__card--product predictive-search-results__product-row';
    item.dataset.productId = String(id);
    item.dataset.available = String(available);
    item.setAttribute('ref', 'resultsItems[]');

    const link = doc.createElement('a');
    link.className = 'predictive-search-results__product-row-link';
    const root = window.Shopify?.routes?.root || '/';
    link.href = new URL(`products/${handle}`, new URL(root, location.origin)).pathname;

    const media = doc.createElement('span');
    media.className = 'predictive-search-results__product-row-media';
    if (image) {
      const img = doc.createElement('img');
      const imageUrl = new URL(image, location.origin);
      imageUrl.searchParams.set('width', '160');
      img.src = imageUrl.toString();
      img.alt = title;
      img.loading = 'lazy';
      img.className = 'predictive-search-results__product-row-image';
      img.width = 64;
      img.height = 64;
      media.append(img);
    }

    const titleNode = doc.createElement('span');
    titleNode.className = 'predictive-search-results__product-row-title';
    titleNode.textContent = title;

    link.append(media, titleNode);
    item.append(link);
    return item;
  }

  /**
   * Shopify predictive search misses some two-letter queries. Name matches from the
   * catalog are merged in and the closest title is placed first.
   * @param {string} markup
   * @param {string | null} phraseMarkup
   * @param {string} searchTerm
   * @param {import('@theme/utilities').CatalogProduct[]} [catalogMatches]
   * @returns {string}
   */
  #rankPredictiveMarkup(markup, phraseMarkup, searchTerm, catalogMatches = []) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const phraseDoc = phraseMarkup ? new DOMParser().parseFromString(phraseMarkup, 'text/html') : null;
    const normalizedQuery = searchTerm.trim().toLowerCase();
    const shortQuery = normalizedQuery.length > 0 && normalizedQuery.length <= 3;

    const seen = new Set();
    /** @type {HTMLElement[]} */
    const items = [];
    const sources = [doc, phraseDoc].filter((source) => source instanceof Document);

    for (const source of sources) {
      for (const item of source.querySelectorAll('.predictive-search-results__card--product')) {
        if (!(item instanceof HTMLElement)) continue;
        if (Theme.settings?.hideSoldOutProducts && item.dataset.available === 'false') continue;
        const title = item.querySelector('.predictive-search-results__product-row-title')?.textContent || '';
        if (shortQuery && !title.toLowerCase().includes(normalizedQuery)) continue;
        const id = item.getAttribute('data-product-id');
        if (!id || seen.has(id)) continue;
        seen.add(id);
        items.push(item);
      }
    }

    for (const product of catalogMatches) {
      if (Theme.settings?.hideSoldOutProducts && !product.available) continue;
      const id = String(product.id);
      if (seen.has(id)) continue;
      seen.add(id);
      items.push(
        this.#createPredictiveProductRow(doc, product.title, product.handle, product.id, product.image, product.available)
      );
    }

    if (!items.length) return markup;

    const list = this.#ensurePredictiveProductList(doc);
    if (!(list instanceof HTMLElement)) return markup;

    const ranked = items
      .map((item, index) => ({
        item,
        index,
        score: scoreProductTitle(
          item.querySelector('.predictive-search-results__product-row-title')?.textContent || '',
          searchTerm
        ),
      }))
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .slice(0, 30);

    list.replaceChildren();
    for (const entry of ranked) {
      list.append(entry.item);
    }

    return doc.body.innerHTML;
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = this.#predictiveSearchUrl(searchTerm);
    const phrase = searchTerm.replace(/"/g, '').trim();
    const phraseUrl = phrase.length >= 3 ? this.#predictiveSearchUrl(`"${phrase}"`) : null;

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    const sectionId = this.dataset.sectionId;
    /** @type {Promise<string | null>[]} */
    const requests = [sectionRenderer.getSectionHTML(sectionId, false, url, abortController.signal)];
    if (phraseUrl && phraseUrl.search !== url.search) {
      requests.push(sectionRenderer.getSectionHTML(sectionId, false, phraseUrl, abortController.signal));
    }

    Promise.all([Promise.all(requests), productsMatchingTitle(searchTerm).catch(() => [])])
      .then(([markups, catalogMatches]) => {
        const resultsMarkup = markups[0];
        const phraseMarkup = markups[1] || null;
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        const rankedMarkup = this.#rankPredictiveMarkup(resultsMarkup, phraseMarkup, searchTerm, catalogMatches);
        morph(predictiveSearchResults, rankedMarkup);

        this.#resetScrollPositions();

        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
